// RequireAuth.tsx
import { useEffect, useState } from "react";
import { useLocation, Navigate } from "react-router-dom";
import { useAuthenticator } from "@aws-amplify/ui-react";
import { hasPermission } from "../../../utils/permissions";
import { NotAuthorized } from "../NotAuthorized";
import { useUserContext } from "../../../contexts/UserContext";
import { iRequireAuthProps } from "../../../types";
import { LoadingSpinner } from "../../LoadingSpinner";


export function RequireAuth({ children, permission, businessAdminOnly }: iRequireAuthProps) {
  const [allowed, setAllowed] = useState<boolean>(false);
  const [evaluated, setEvaluated] = useState<boolean>(false);

  const location = useLocation();
  const { route, user } = useAuthenticator((context) => [context.route, context.user]);
  const { companyUser, companyOwner, businessAdmin } = useUserContext();

  useEffect(() => {
    if (!user) {
      setEvaluated(true);
      setAllowed(false);
      return;
    }

    // BusinessAdmin-only route — redirect non-BusinessAdmin users
    if (businessAdminOnly) {
      setAllowed(businessAdmin != null);
      setEvaluated(true);
      return;
    }

    // No permission required — just needs to be logged in
    if (!permission) {
      setAllowed(true);
      setEvaluated(true);
      return;
    }

    // Single unified check: businessAdmin bypass + role + explicit overrides.
    // An array allows any one of the listed permissions.
    const required = Array.isArray(permission) ? permission : [permission];
    setAllowed(required.some((item) => hasPermission(companyOwner, companyUser, item, businessAdmin)));
    setEvaluated(true);
  }, [companyUser, companyOwner, businessAdmin, permission, businessAdminOnly, user]);

  if (route === "setup" && !user) {
    return <Navigate to="/?showLogin=true" state={{ from: location }} />;
  }

  // Redirect non-BusinessAdmin users to home for businessAdminOnly routes
  if (evaluated && !allowed && businessAdminOnly) {
    return <Navigate to="/" replace />;
  }

  return (
    <LoadingSpinner
      isLoading={!evaluated}
      keepMounted={evaluated}
      content={allowed ? children : <NotAuthorized />}
    />
  );
}
